import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import { useChannel, type RealtimeEvent } from '@/shared/realtime';

import { kinds, type EnvKey } from './scope';

/** Канал окружения сайта — зеркало access.SiteChannel на бэкенде. */
export function siteChannel(siteId: string, env: EnvKey): string {
  return `site:${siteId}:${kinds[env]}`;
}

/** Канал записи — зеркало access.RecordChannel. */
export function recordChannel(recordId: string): string {
  return `record:${recordId}`;
}

/**
 * Живые списки и счётчики окружения: пока человек в сайте, чужие правки
 * долетают сами — без F5.
 *
 * Событие говорит только «что изменилось», и по нему помечаются устаревшими
 * ровно те запросы, которые это показывают. Перечитываются из них только
 * видимые на экране — остальные при следующем открытии.
 */
export function useLiveSite(siteId: string | undefined, env: EnvKey | undefined): void {
  const queryClient = useQueryClient();

  useChannel(
    siteId === undefined || env === undefined ? undefined : siteChannel(siteId, env),
    (event) => {
      if (event.event === 'changed' && siteId !== undefined) {
        invalidateFor(queryClient, siteId, event);
      }
    },
  );
}

function invalidateFor(queryClient: QueryClient, siteId: string, event: RealtimeEvent): void {
  const invalidate = (...queryKey: unknown[]) => {
    void queryClient.invalidateQueries({ queryKey });
  };

  // Главная собирает всё понемногу: любая правка содержимого её касается.
  invalidate('home', siteId);

  switch (event.data['target_type']) {
    case 'record':
      // Списки записей и счётчики коллекций в меню. Открытую запись
      // обновляет её собственный канал.
      invalidate('records');
      invalidate('content-types', siteId);

      break;
    case 'type':
    case 'field':
    case 'schema':
      // Схема меняет и меню, и колонки списков, и формы. Перенос схемы —
      // то же самое, только сразу много.
      invalidate('schema-transfer', siteId);
      invalidate('content-types', siteId);
      invalidate('content-type');
      invalidate('block-types', siteId);
      invalidate('records');

      break;
    case 'media':
    case 'folder':
      invalidate('media', siteId);
      invalidate('media-folders', siteId);
      invalidate('media-item');
      invalidate('media-usage');

      break;
    case 'constant':
      invalidate('constants', siteId);

      break;
    case 'menu':
      invalidate('menus', siteId);

      break;
    case 'site':
      invalidate('site', siteId);
      invalidate('environments', siteId);
      invalidate('sites');
      invalidate('switcher');

      break;
    default:
      break;
  }
}
